import { useState, useLayoutEffect, useEffect, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import rightIcon from "../../images/rightIcon.svg";
import { trimText } from "../../Constants/utility";
import { Label } from "../StyledComponents/StyledComponents";
import { WithTooltip } from "../StyledComponents/WithTooltip";
import { ArticlesLoader } from "../Loaders/Articles";
import ScrollToTop from "../../Components/ScrollToTop";
import { SubCategories } from "../Categories/SubCategories";
import { ArticlesCard, SubCategoryCard } from "../Categories/FlattenedCards";
import { useSubCategories } from "../../hooks/apis/useSubCategories";
import { SubCategoriesLoader } from "../Loaders/SubCategories";
import { autoCapitaliseFirstLetter } from "../../Constants/utility";
import { KBContext, AutherContext } from "../../Context";
import { BreadCrumbs } from "../BreadCrumbs";
import { useSlugTitles } from "../../hooks/useSlugTitles";
import { sendGainsightEvent, KB_WEBSITE_GAINSIGHT_EVENTS } from "../../gainSightIntegration";
import { isFeatureEnabled } from "../../utils";
import { FTR_KB_FLATTENED_CATEGORIES } from "../../Constants";

/* No left indent, max-width so content doesn't span full width, centered */
const ArticleListContainer = styled.section`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  box-sizing: border-box;
  padding-left: 0;
  padding-right: 0;
  position: relative;
`;

/* Article row: flex row, icon + text + chevron, vertically centered */
const ArticleBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  gap: 16px;
  box-sizing: border-box;

  &:hover {
    border-radius: 12px;
    border: 1px solid var(--slate-200, #E2E8F0);
    background: var(--colors-Color, #FFF);
    box-shadow: 0 0 2px -1px rgba(0, 0, 0, 0.01), 0 1px 3px 0 rgba(0, 0, 0, 0.10);
  }
`;

/* Article icon: 24px, brand color */
const ArticleIcon = styled.span`
  font-size: 24px;
  width: 24px;
  height: 24px;
  color: ${(p) => p.$color || "#EA580C"};
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/* Article label: Inter Regular 16/24 slate/900 */
const ArticleLabel = styled(Label)`
  flex: 1 0 0;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #0f172a;
  margin: 0;
  padding: 0;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
`;

/* Chevron at end of article name — color via mask so fill can be set */
const ChevronRight = styled.span`
  display: inline-block;
  width: 7.778px;
  height: 12.728px;
  flex-shrink: 0;
  background-color: var(--slate-400, #94A3B8);
  mask-image: url(${rightIcon});
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url(${rightIcon});
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
`;

/* Flex column — title + description, left-aligned, gap 12px */
const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(p) => (p.$flattened ? "8px" : "12px")};
  width: 100%;
  margin-bottom: 32px;
`;

/* Heading/sm/medium: Inter Medium, 20px, 500, line-height 24px, slate/900 */
const CategoryTitle = styled.h1`
  margin: 0;
  padding: 0;
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0;
  color: #0f172a;
`;

/* Body/md/regular: Inter Regular, 16px, 400, line-height 24px */
const CategoryDescription = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${(p) => (p.$flattened ? "#475569" : "#0f172a")};
`;

/* Flex column — article rows stacked with 20px gap */
const ArticlesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  position: relative;
  min-height: 200px;
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 32px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 60px;
  min-height: 200px;
`;

export default function ArticleListing({
  articles,
  isLoading,
  handleArticleClick,
  selectedCategory,
  kbdata,
}) {
  const [maxLength, setMaxLength] = useState(90);
  const originalDocTitle = useRef('');
  const { setArticleTitle, availableFeatures } = useContext(KBContext);
  const { userDetails } = useContext(AutherContext);
  const { search } = useLocation();
  const isFlattenedUI = isFeatureEnabled(FTR_KB_FLATTENED_CATEGORIES, availableFeatures);
  const fullPath = selectedCategory?.fullPath?.split('/') ?? [];
  const categorySlugs = fullPath.slice(0, -1);
  const { slugTitleMap, isLoading: isSlugTitlesLoading } = useSlugTitles(categorySlugs);
  const { data: subCategories = [], isLoading: isSubCategoriesLoading } = useSubCategories({
    categorySlug: isFlattenedUI ? selectedCategory?.slug : undefined,
  });

  useLayoutEffect(() => {
    if (window.innerWidth > 1100) {
      setMaxLength(90);
    } else {
      setMaxLength(50);
    }
  });

  //maintain the order of useEffect

  useEffect(() => {
    setArticleTitle(null);
  }, []);

  useEffect(() => {
    originalDocTitle.current = document.title;
    return () => {
      document.title = originalDocTitle.current;
    };
  }, []);

  useEffect(() => {
    const docTitle = selectedCategory?.title;
    if (!docTitle) return;
    else {
      document.title = autoCapitaliseFirstLetter(docTitle);
    }
  });

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }
    const user_id = userDetails?.userDetails?.userId;
    const user_group_id = userDetails?.userDetails?.usergroupId;
    const location = window.location;
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.CATEGORY_PAGE_VIEWED, {
      user_id,
      user_group_id,
      category_slug: selectedCategory?.slug,
      category_name: selectedCategory?.title,
      page_url: location.href,
      page_base_url: `${location.protocol}//${location.host}`,
    })
  }, [userDetails, selectedCategory]);

  const slugNameMap = {
    ...slugTitleMap,
    ...(selectedCategory?.slug ? { [selectedCategory.slug]: selectedCategory.title } : null),
  };

  const brandColor = kbdata?.brandColor || "#EA580C";

  return (
    <ArticleListContainer>
      <ScrollToTop />
      <BreadCrumbs
        fullPath={selectedCategory?.fullPath}
        isLoading={isSlugTitlesLoading}
        slugNameMap={slugNameMap}
        style={{ marginTop: 0, marginBottom: isFlattenedUI ? "32px" : "20px" }}
      />
      <TitleSection $flattened={isFlattenedUI}>
        <CategoryTitle>{selectedCategory?.title}</CategoryTitle>
        <CategoryDescription $flattened={isFlattenedUI}>
          {selectedCategory?.description}
        </CategoryDescription>
      </TitleSection>

      {isFlattenedUI ? (
        <CardsWrapper>
          <ArticlesCard
            articles={articles}
            isLoading={isLoading}
            loaderCount={selectedCategory?.immediateArticleCount ?? 5}
            getArticlePath={(article) => `/${selectedCategory?.slug}/${article.slug}` + search}
          />
          {isSubCategoriesLoading ? (
            <SubCategoriesLoader count={selectedCategory?.immediateSubcategoryCount ?? 4} />
          ) : (
            subCategories.map((subCategory) => (
              <SubCategoryCard key={subCategory.slug} subCategory={subCategory} />
            ))
          )}
        </CardsWrapper>
      ) : (
        <>
          <SubCategories
            selectedCategory={selectedCategory}
            style={{ marginBottom: "20px" }}
          />
          <ArticlesWrapper>
            {isLoading ? (
              <ArticlesLoader count={selectedCategory?.immediateArticleCount ?? 5} />
            ) : (
              articles.map((article) => (
                <ArticleBar
                  key={article?.slug}
                  onClick={() => handleArticleClick(article)}
                >
                  <ArticleIcon className="material-icons-outlined" $color={brandColor}>
                    description
                  </ArticleIcon>
                  <WithTooltip
                    isNeeded={article?.title?.length > maxLength}
                    className="bottom-center"
                    content={article?.title}
                    size="300px"
                  >
                    <ArticleLabel as="span">
                      {trimText(article?.title, maxLength)}
                    </ArticleLabel>
                  </WithTooltip>
                  <ChevronRight />
                </ArticleBar>
              ))
            )}
          </ArticlesWrapper>
        </>
      )}
    </ArticleListContainer>
  );
}
